import { format, subDays } from "date-fns";
import type { DateRange } from "react-day-picker";
import { CalendarDays, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/utils/common.utils";

interface DateRangeFilterProps {
  value?: DateRange;
  onChange: (value: DateRange | undefined) => void;
  numberOfMonths?: number;
  showPreset?: boolean;
  testId?: string;
  className?: string;
}

export default function DateRangeFilter({ value, onChange, numberOfMonths = 2, showPreset = false, testId, className }: DateRangeFilterProps) {
  const label = value?.from
    ? value.to
      ? `${format(value.from, "MMM d")} – ${format(value.to, "MMM d")}`
      : format(value.from, "MMM d, yyyy")
    : "Date range";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="dashboardOutline" size="lg" className={cn("h-11 shrink-0 !rounded-xl", className)} data-testid={testId}>
          <CalendarDays data-icon="inline-start" /> {label} <ChevronDown data-icon="inline-end" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto rounded-xl border-dash-border p-0">
        <Calendar mode="range" selected={value} onSelect={onChange} defaultMonth={value?.from} numberOfMonths={numberOfMonths} disabled={{ after: new Date() }} initialFocus />
        <div className="flex items-center justify-end gap-2 border-t border-dash-border p-3">
          {value?.from && <Button type="button" variant="ghost" size="sm" onClick={() => onChange(undefined)}>Clear</Button>}
          {showPreset && <Button type="button" variant="outline" size="sm" onClick={() => onChange({ from: subDays(new Date(), 29), to: new Date() })}>Last 30 days</Button>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
